import clsx from 'clsx';
import { Button, ModalPopup, Stack, Text, Title } from 'components';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getMedicalVisitsPreview,
  getMedicalVisitsTypes,
  getMedicalVisitsStatuses
} from 'store/medicalVisits/medicalVisitsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { axiosPrivate } from 'modules/networkTools/networkTools';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';

import style from './medicalVisits.module.scss';
import MedicalVisitForm from './medicalVisitForm';
import MedicalVisitsFilters from './medicalVisitsFilters';
import MedicalVisitsTable from './medicalVisitsTable';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const downloadFile = async (fileUploadUrl: string, text: string): Promise<any> => {
  const response = await axiosPrivate.get(fileUploadUrl);

  // convert to blob and set download link
  const file = new Blob([response.data], { type: 'text/csv' });
  const element = document.createElement('a');
  element.href = URL.createObjectURL(file);
  element.download = `${text}_${dayjs().format('M-D-YY')}.csv`;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);

  return null;
};

const MedicalVisitsModal: FC<Props> = ({ open, setOpen }) => {
  const { data } = useAppSelector((state) => state.clients.client);
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const [showForm, setShowForm] = useState(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const { enableFileUpload } = useFlags();
  const canEditMedicalVisits = enableFileUpload
    ? data?.medicalVisitsEditingAllowed
    : data?.clinicalEncountersEditingAllowed;

  const text = enableFileUpload ? 'Medical Visits' : 'Clinical Encounters';
  const fileUploadUrl = `client/${clientId}/${
    enableFileUpload ? 'medical-visits' : 'clinical-encounters'
  }/download`;

  useEffect(() => {
    if (clientId) {
      dispatch(getMedicalVisitsTypes({ clientId }));
      dispatch(getMedicalVisitsStatuses({ clientId }));
    }
  }, []);

  const handleModalClose = () => {
    if (clientId) dispatch(getMedicalVisitsPreview({ clientId }));
    setOpen(false);
  };

  return (
    <ModalPopup
      open={open}
      customClassInsideContainer={clsx(style.modalWrapper)}
      handleClose={() => {
        if (formDirty) {
          setUnsavedChangesModalOpen(true);
        } else {
          handleModalClose();
        }
      }}
    >
      <Stack width="100%" height="80vh">
        <Stack width="100%" flexDirection="row" justifyContent="space-between">
          <Stack
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="16px"
            mb="24px"
            mt="4px"
          >
            <Title text={text} type="h3" />
            {!canEditMedicalVisits && (
              <Text size="large" className={style.readOnly} weight="regular">
                Read-only
              </Text>
            )}
          </Stack>
          <Stack flexDirection="row" gap="12px" mr="48px" mt="-5px">
            <Button
              type="button"
              theme="transparent"
              size="small"
              shape="circle"
              icon={<span className={clsx('material-icons')}>download</span>}
              handleOnClick={() => clientId && downloadFile(fileUploadUrl, text)}
            />
            {canEditMedicalVisits && (
              <Button
                size="small"
                shape="circle"
                handleOnClick={() => setShowForm(true)}
                disabled={showForm}
                icon={<span className="material-icons-outlined">add</span>}
                theme="primary"
                customStyleClass={style.iconButtonAdd}
              />
            )}
          </Stack>
        </Stack>
        <Stack width="100%" height="93%">
          {showForm && (
            <MedicalVisitForm
              handleCancel={() => {
                setShowForm(false);
                setFormDirty(false);
              }}
              setFormDirty={setFormDirty}
              unsavedChangesModalOpen={unsavedChangesModalOpen}
              setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
              handleModalClose={handleModalClose}
            />
          )}
          <MedicalVisitsFilters />
          <MedicalVisitsTable isDisabled={showForm} />
        </Stack>
      </Stack>
    </ModalPopup>
  );
};

export default MedicalVisitsModal;
